import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import CustomGroupsGrid from './customGroupsGrid.component';
import SelectedCustomGroupDetails from './selectedCustomGroupDetails.component';
import DeleteCustomGroupModal from './deleteCustomGroupModal.component';
import { withPermissions, permissions } from '../../shared/authorization';
import {
  fetchCustomGroups,
  removeCustomGroup,
  fetchCustomGroupItemTypes,
  selectCustomGroup,
  hideDeleteCustomGroupModal,
  fetchGroupItems,
  exitEditMode,
  clearCreatedCustomGroup,
  hideDiscardChangesConfirmModal,
  showDiscardChangesConfirmModal,
  fetchUserPrivileges,
} from './store/actions';

import {
  modalDataSelector,
} from '../../shared/modals/modals.selectors';

import {
  readOnlySelector,
  selectedCustomGroupSelector,
  selectedCustomGroupIdSelector,
  isSelectedCustomGroupEditableSelector,
  isCreatingNewCustomGroupSelector,
  isCustomGroupModifiedSelector,
  isArchiveAdminSelector,
} from './store/selectors';

import {
  CustomGroupsPropTypes,
  CustomGroupPropTypes,
} from '../../shared/propTypes/customGroups';

import ModalNames from './modalNames';
import ConfirmDialog from '../../shared/confirmDialog.component';
import { CustomGroupDiscardChangesModalDataPropTypes } from './propTypes';
import { assignablePermissionTypes } from '@common/constants/assignablePermissions';

class CustomGroupsPage extends PureComponent {
  static propTypes = {
    fetchCustomGroups: PropTypes.func.isRequired,
    removeCustomGroup: PropTypes.func.isRequired,
    fetchCustomGroupItemTypes: PropTypes.func.isRequired,
    fetchUserPrivileges: PropTypes.func.isRequired,
    fetchGroupItems: PropTypes.func.isRequired,
    selectCustomGroup: PropTypes.func.isRequired,
    isArchiveAdmin: PropTypes.bool.isRequired,
    selectedCustomGroup: CustomGroupPropTypes,
    selectedCustomGroupId: PropTypes.number,
    createdCustomGroup: CustomGroupPropTypes,
    exitEditMode: PropTypes.func.isRequired,
    customGroups: CustomGroupsPropTypes,
    customGroupsLoading: PropTypes.bool.isRequired,
    customGroupItemsLoading: PropTypes.bool.isRequired,
    hideDeleteCustomGroupModal: PropTypes.func.isRequired,
    deleteModalData: PropTypes.shape({
      show: PropTypes.bool.isRequired,
      data: PropTypes.shape({
        customGroupId: PropTypes.number.isRequired,
      }),
    }).isRequired,
    isDeletingCustomGroupInProgress: PropTypes.bool.isRequired,
    isSelectedCustomGroupEditable: PropTypes.bool.isRequired,
    readOnly: PropTypes.bool.isRequired,
    isCreatingNewCustomGroup: PropTypes.bool.isRequired,
    isCustomGroupModified: PropTypes.bool.isRequired,
    showDiscardChangesConfirmModal: PropTypes.func.isRequired,
    hideDiscardChangesConfirmModal: PropTypes.func.isRequired,
    discardChangesConfirmDialogData: CustomGroupDiscardChangesModalDataPropTypes,
  };

  state = {
    isFormDirty: false,
  };

  async componentDidMount() {
    const {
      fetchCustomGroups,
      fetchGroupItems,
      fetchCustomGroupItemTypes,
      fetchUserPrivileges,
    } = this.props;

    fetchGroupItems();
    fetchCustomGroupItemTypes();
    fetchCustomGroups({ selectFirstItem: true });
    fetchUserPrivileges(assignablePermissionTypes.ArchiveAdmin);
  }

  hideDeletingModal = () => {
    this.props.hideDeleteCustomGroupModal();
  };

  selectCustomGroup = customGroupToChooseId => {
    const {
      selectCustomGroup,
      selectedCustomGroupId,
      exitEditMode,
    } = this.props;

    const customGroupAlreadySelected = customGroupToChooseId === selectedCustomGroupId;

    if (customGroupAlreadySelected) {
      return;
    }

    selectCustomGroup(customGroupToChooseId);
    exitEditMode();
    clearCreatedCustomGroup();
  };

  onFormDirtyChange = isFormDirty => this.setState(() => ({
    isFormDirty,
  }));

  render() {
    const {
      customGroups,
      customGroupsLoading,
      customGroupItemsLoading,
      selectedCustomGroup,
      createdCustomGroup,
      deleteModalData,
      isSelectedCustomGroupEditable,
      isCreatingNewCustomGroup,
      readOnly,
      discardChangesConfirmDialogData,
      showDiscardChangesConfirmModal,
      hideDiscardChangesConfirmModal,
      isArchiveAdmin,
    } = this.props;

    return (
      <div className="custom-groups-page">
        <div className="page">
          <div className="container-fluid">
            <div className="row no-gutters page-content-container">
              <div className="col">
                <div className="d-flex flex-row justify-content-between">
                  <h5>Custom Groups</h5>
                </div>
              </div>
            </div>

            <div className="row mb-2">
              <div className="col">
                <div className="custom-groups-grid page-content-container">
                  <CustomGroupsGrid
                    customGroups={customGroups}
                    customGroupsLoading={customGroupsLoading}
                    selectCustomGroup={this.selectCustomGroup}
                    isCreatingNewCustomGroup={isCreatingNewCustomGroup}
                    selectedCustomGroup={createdCustomGroup || selectedCustomGroup}
                    isCustomGroupModified={this.state.isFormDirty}
                    showDiscardChangesConfirmModal={showDiscardChangesConfirmModal}
                    hideDiscardChangesConfirmModal={hideDiscardChangesConfirmModal}
                    discardChangesConfirmDialogData={discardChangesConfirmDialogData}
                  />

                  <DeleteCustomGroupModal
                    entityToRemove={deleteModalData.data}
                    isVisible={deleteModalData.show}
                  />
                </div>
              </div>
            </div>

            <div className="row mb-2">
              <div className="col">
                <div className="custom-groups-details page-content-container">
                  <SelectedCustomGroupDetails
                    selectedCustomGroup={createdCustomGroup || selectedCustomGroup}
                    customGroupItemsLoading={customGroupItemsLoading}
                    isCreatedGroup={!!createdCustomGroup}
                    editable={!!createdCustomGroup || isSelectedCustomGroupEditable}
                    readOnly={readOnly}
                    isArchiveAdmin={isArchiveAdmin}
                    onFormDirtyChange={this.onFormDirtyChange}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <ConfirmDialog
          onConfirm={discardChangesConfirmDialogData.data && discardChangesConfirmDialogData.data.onConfirm}
          onCancel={hideDiscardChangesConfirmModal}
          isVisible={discardChangesConfirmDialogData.show}
        />
      </div>
    );
  }
}

function mapStateToProps(state) {
  const { administration } = state;

  return {
    customGroups: administration.customGroups.customGroups,
    fetchingUserPrivileges: administration.customGroups.fetchingUserPrivileges,
    customGroupsLoading: administration.customGroups.customGroupsLoading,
    customGroupItemsLoading: administration.customGroups.customGroupItemsLoading,
    createdCustomGroup: administration.customGroups.createdCustomGroup,
    selectedCustomGroup: selectedCustomGroupSelector(state),
    selectedCustomGroupId: selectedCustomGroupIdSelector(state),
    deleteModalData: modalDataSelector(state, ModalNames.confirmDeletingCustomGroup),
    isDeletingCustomGroupInProgress: administration.customGroups.isDeletingCustomGroupInProgress,
    readOnly: readOnlySelector(state),
    isSelectedCustomGroupEditable: isSelectedCustomGroupEditableSelector(state),
    isCreatingNewCustomGroup: isCreatingNewCustomGroupSelector(state),
    isCustomGroupModified: isCustomGroupModifiedSelector(state),
    isArchiveAdmin: isArchiveAdminSelector(state),
    discardChangesConfirmDialogData: modalDataSelector(state, ModalNames.confirmDiscardCustomGroupChanges),
  };
}

export default withPermissions(permissions.CUSTOM_GROUPS)(connect(
  mapStateToProps,
  {
    fetchCustomGroups,
    removeCustomGroup,
    selectCustomGroup,
    hideDeleteCustomGroupModal,
    fetchCustomGroupItemTypes,
    fetchGroupItems,
    exitEditMode,
    clearCreatedCustomGroup,
    showDiscardChangesConfirmModal,
    hideDiscardChangesConfirmModal,
    fetchUserPrivileges,
  }
)(CustomGroupsPage));
